<script setup lang="ts">
import IconMaterialSymbolsFolder from '~icons/material-symbols/folder'
import IconMaterialSymbolsCloseRounded from '~icons/material-symbols/close-rounded'
import { openProjectEdit, openProjectFacilities } from '@/app/components/projects/dialog/DialogStates'
import { useServiceProject } from '@/api/services/project.service'
import { useServiceFacility } from '@/api/services/facility.service'
import { useFetchProjects } from '@/app/composables/fetch/useFetchProjects'
import { useStoreProject } from '@/stores/project'
import { useStoreFacility } from '@/stores/facility'
import { useStoreApp } from '@/stores/app'
import { useToolStore } from '@/stores/tool'
import { useTerminology } from '@/shared/composables/useTerminology'
import { router } from '@/router'
import { computed, onMounted, ref, watch } from 'vue'

import ProjectShareDialog from '@/app/components/features/share/ProjectShareDialog.vue'
import ProjectEditDialog from '@/app/components/projects/dialog/ProjectEditDialog.vue'
import EditAccessDialog from '@/app/components/projects/dialog/EditAccessDialog.vue'
import ProjectFacilitiesDialog from '@/app/components/projects/dialog/ProjectFacilitiesDialog.vue'
import ProjectConceptTable from '@/app/components/projects/ProjectConceptTable.vue'
import ProjectConceptEmptyState from '@/app/components/projects/ProjectConceptEmptyState.vue'
import ModuleAccessDeniedModal from '@/app/components/access/ModuleAccessDeniedModal.vue'
import LocationSidebar from '@/app/components/projects/LocationSidebar.vue'
import FacilityOverview from '@/app/components/projects/facilities/FacilityOverview.vue'
import Icon from '@/shared/components/Icon.vue'
import Button from '@/shared/components/Button.vue'

import { appToast } from '@/shared/components/toast/toast'

import type { Uuid4 } from '§C/types/system.type'
import { OperationalModel } from '§M/types/enumerated.type'
import { ElementVisibility } from '§§M/main/types/enumerated.type'

const storeApp = useStoreApp()
const storeProject = useStoreProject()
const storeFacility = useStoreFacility()
const storeTool = useToolStore()
const { terms } = useTerminology()

const sortedProjects = storeProject.projectListSorted()

// Scoped to the tool picked on the Choose a tool page. Projects carrying no
// operational_model show under either tool so they stay reachable.
const toolProjects = computed(() => {
  const tool = storeTool.selectedTool
  if (!tool) return sortedProjects.value
  return sortedProjects.value.filter(
    (project) => !project.operational_model || project.operational_model === tool
  )
})

const { readAllFacilities } = useServiceFacility()
const { fetchData: fetchFacilities } = readAllFacilities()

// Location (facility) filter for the projects list. null = all locations.
const selectedLocation = ref<Uuid4 | null>(null)

// Switching tool from the statusbar keeps this page mounted, and the location
// picked under the previous tool usually holds no projects under the new one.
watch(
  () => storeTool.selectedTool,
  () => {
    selectedLocation.value = null
  }
)

// Per-location project counts derived from the access-filtered project list, so
// counts always reflect what the current user can actually see. A project linked
// to several facilities is counted under each.
const locationCounts = computed(() => {
  const counts = new Map<Uuid4, number>()
  for (const project of toolProjects.value) {
    for (const facility of project.facilities ?? []) {
      counts.set(facility.uuid, (counts.get(facility.uuid) ?? 0) + 1)
    }
  }
  return counts
})

// All org locations (from the facility list endpoint) so even locations with no
// visible projects are shown, with counts merged from the project list.
const locations = computed(() =>
  storeFacility.facilities
    .map((facility) => ({
      id: facility.uuid,
      title: facility.title,
      count: locationCounts.value.get(facility.uuid) ?? 0
    }))
    .sort((a, b) => a.title.localeCompare(b.title))
)

const selectedLocationTitle = computed(
  () => locations.value.find((loc) => loc.id === selectedLocation.value)?.title ?? ''
)

// Table rows scoped to the selected location (projects linked to that facility).
const visibleProjects = computed(() => {
  if (selectedLocation.value === null) return toolProjects.value
  return toolProjects.value.filter((project) =>
    (project.facilities ?? []).some((facility) => facility.uuid === selectedLocation.value)
  )
})

const newProject = () => {
  // Navigate to optional parameter route without UUID for new projects
  router.push({ name: '/(with-statusbar)/projects/create/[[projectUuid]]/' })
}

onMounted(() => {
  fetchProjects()
  fetchFacilities()
  storeApp.setPageHeader({ header: terms.value.Projects })
})

const shareProjectId = ref('')
const showProjectShareDialog = ref(false)

const moduleDeniedVisible = ref(false)
const moduleDeniedModule = ref<string | null>(null)
const acknowledgedModules = ref<Set<string>>(new Set())

const moduleLabel = (commonId: string) => commonId.charAt(0).toUpperCase() + commonId.slice(1)

// First click on a row with a given denied module -> open the dialog explaining
// the situation. Subsequent clicks on rows of the same module in this session
// degrade to a brief toast so the user isn't dialog-spammed when scanning the list.
const handleModuleDenied = (data: { operational_model?: string | null }) => {
  const module = data.operational_model ?? null
  if (!module) return

  if (acknowledgedModules.value.has(module)) {
    appToast.warning('Access restricted', {
      detail: `Your organization does not have access to the ${moduleLabel(module)} module.`
    })
    return
  }

  acknowledgedModules.value.add(module)
  moduleDeniedModule.value = module
  moduleDeniedVisible.value = true
}

const handleShareProject = (uuid: string) => {
  shareProjectId.value = uuid
  showProjectShareDialog.value = true
}

const { pendingProjects, statusProjects, fetchProjects } = useFetchProjects()

const { deleteProject, pinProject, unpinProject, duplicateProject, updateProject } = useServiceProject()

// Edit-access (visibility) dialog state
const accessEditItem = ref<{ uuid: Uuid4; title: string; visibility?: ElementVisibility } | null>(null)
const showAccessDialog = ref(false)

const handleAccessEdit = (data: (typeof sortedProjects.value)[number]) => {
  accessEditItem.value = { uuid: data.uuid, title: data.title, visibility: data.visibility }
  showAccessDialog.value = true
}

const handleAccessSave = async (visibility: ElementVisibility) => {
  if (!accessEditItem.value) return
  // handleApiError already toasts on failure; don't follow it with a success toast
  const updated = await updateProject({ uuid: accessEditItem.value.uuid, visibility })
  if (!updated) return
  appToast.success(visibility === ElementVisibility.PRIVATE ? 'Made private' : 'Shared with organization', {
    duration: 2200
  })
}


const handleDeleteProject = (uuid: Uuid4): void => {
  deleteProject(uuid)
}

const openProject = (uuid: Uuid4) => {
  const project = storeProject.stateProjects.projects.get(uuid)

  // Check access before navigating
  if (project?.user_access && !project.user_access.has_access) {
    appToast.warning('Access denied', { detail: `You do not have access to this ${terms.value.project}.` })
    return
  }

  // Nexus projects always go directly to concepts
  if (project?.operational_model === OperationalModel.NEXUS) {
    router.push({ name: '/(with-statusbar)/projects/[projectUuid]/concepts/', params: { projectUuid: uuid } })
    return
  }

  if (project?.status === 'draft') {
    // Use optional parameter route with UUID for existing draft projects
    // If project is axios and draft, go to create flow for setup
    if (project.operational_model === OperationalModel.AXIOS) {
      router.push({ name: '/(with-statusbar)/projects/create/[[projectUuid]]/', params: { projectUuid: uuid } })
    } else {
      router.push({ name: '/(with-statusbar)/projects/create/[[projectUuid]]/', params: { projectUuid: uuid } })
    }
  } else {
    router.push({ name: '/(with-statusbar)/projects/[projectUuid]/concepts/', params: { projectUuid: uuid } })
  }
}

definePage({
  meta: {
    label: 'Projects',
    icon: IconMaterialSymbolsFolder
  }
})
</script>

<template>
  <!-- Axios lists its facilities as cards holding their plans (#2690) -->
  <FacilityOverview
    v-if="storeTool.selectedTool === OperationalModel.AXIOS"
    :facilities="toolProjects"
    :loading="pendingProjects"
  />

  <div
    v-else
    class="flex min-h-0 flex-1"
  >
    <LocationSidebar
      :locations="locations"
      :selected="selectedLocation"
      :total-count="toolProjects.length"
      @select="selectedLocation = $event"
    />

    <div class="flex min-w-0 flex-1 flex-col">
      <div class="flex items-center gap-3 px-5 pt-4 pb-3">
        <Icon
          :icon="IconMaterialSymbolsFolder"
          class="h-5 w-5"
        />
        <template v-if="selectedLocation === null">
          <span class="text-foreground text-lg font-semibold">All locations</span>
          <span class="text-muted-foreground text-sm">
            {{ toolProjects.length }} {{ toolProjects.length === 1 ? terms.project : terms.projects }}
            across {{ locations.length }} {{ locations.length === 1 ? 'location' : 'locations' }}
          </span>
        </template>
        <template v-else>
          <span class="text-foreground text-lg font-semibold">{{ selectedLocationTitle }}</span>
          <span class="text-muted-foreground text-sm">
            {{ visibleProjects.length }} {{ visibleProjects.length === 1 ? terms.project : terms.projects }}
          </span>
          <Button
            variant="text"
            size="small"
            type="button"
            :icon="IconMaterialSymbolsCloseRounded"
            label="Clear filter"
            class="text-muted-foreground hover:text-foreground ml-1 h-auto gap-1 px-2 py-0.5 text-xs font-normal"
            @click="selectedLocation = null"
          />
        </template>
      </div>

      <!-- Scope tab (prototype's ScopeTabs): a single "All projects" tab with the
           active teal underline sitting on the row border. -->
      <div class="border-border flex items-center border-b px-5">
        <div class="border-primary text-foreground -mb-px flex items-center gap-2 border-b-2 px-1 py-2.5 text-sm font-medium">
          <Icon
            :icon="IconMaterialSymbolsFolder"
            class="h-4 w-4"
          />
          All {{ terms.projects }}
          <span class="bg-primary/15 text-primary rounded px-1.5 py-0.5 text-xs font-semibold">
            {{ visibleProjects.length }}
          </span>
        </div>
      </div>

      <div class="flex min-h-0 flex-1 flex-col">
        <ProjectConceptTable
          :items="visibleProjects"
          :loading="pendingProjects"
          resource="project"
          :on-delete="(data) => handleDeleteProject(data.uuid)"
          @duplicate="(data) => duplicateProject(data.uuid)"
          @row-click="(data) => openProject(data.uuid)"
          @module-denied="handleModuleDenied"
          @edit="(data) => openProjectEdit(data.uuid)"
          @facilities="(data) => openProjectFacilities(data.uuid)"
          @share="(data) => handleShareProject(data.uuid)"
          @unpin="(data) => unpinProject(data.uuid)"
          @pin="(data) => pinProject(data.uuid)"
          @access-edit="handleAccessEdit"
          @create-new="newProject"
        >
          <template #empty>
            <ProjectConceptEmptyState
              resource="project"
              @create="newProject"
            />
          </template>
        </ProjectConceptTable>
      </div>
    </div>
  </div>

  <ProjectEditDialog />
  <EditAccessDialog
    v-model="showAccessDialog"
    :item="accessEditItem"
    @save="handleAccessSave"
  />
  <ProjectFacilitiesDialog />
  <ProjectShareDialog
    v-model="showProjectShareDialog"
    :projectId="shareProjectId"
  />
  <ModuleAccessDeniedModal
    v-model="moduleDeniedVisible"
    :module="moduleDeniedModule"
  />
</template>
